/**
 * Default configuration values and entity branding presets.
 *
 * Each Wave 1 entity (EEC, DCCS, EAS, HMI Main) has a complete
 * branding preset including colors, typography, font family,
 * and button styling. Unknown entities fall back to the HMI Main
 * corporate theme (blue, system fonts, rounded buttons).
 */

import type {
  EntityBranding,
  ResolvedWidgetConfig,
  TypographyConfig,
  WidgetConfig,
} from "../types";

/** Default PDPA consent message shown before personal data collection */
const DEFAULT_CONSENT_MESSAGE =
  "To help you with your request, I may need to collect personal information " +
  "such as your name, contact number, and NRIC. This information will be " +
  "handled in accordance with the Personal Data Protection Act (PDPA). " +
  "Please review our Terms of Use before proceeding.";

/** Default TOU URL (HMI Medical Group corporate site).
 * Matches backend default `HMI_TOU_URL` in `src/hana/engines/escalation.py`.
 * HMI canonical domain confirmed 2026-05-06; see journal
 * `0131-DECISION-hmi-canonical-domain-confirmed.md`. */
const DEFAULT_TOU_URL = "https://www.hmimedical.com/hana-terms-of-use";

/**
 * Entity-specific TOU URLs. Each HMI entity may have its own terms page.
 * These are initial values; actual URLs will be confirmed during entity onboarding (M09).
 */
const ENTITY_TOU_URLS: Record<string, string> = {
  eec: "https://www.eyeandent.com.sg/terms-of-use",
  dccs: "https://www.dccs.com.sg/terms-of-use",
  eas: "https://www.eas.com.sg/terms-of-use",
  hmi_main: "https://www.hmimedical.com/hana-terms-of-use",
};

/** System font stack used as fallback when an entity does not specify one. */
export const SYSTEM_FONT_STACK =
  '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';

/** Outfit font family with system fallback (used by HANA II Figma frames) */
export const OUTFIT_FONT_STACK = `"Outfit", ${SYSTEM_FONT_STACK}`;

/** Legacy Inter font stack retained for backwards-compatible imports. */
export const INTER_FONT_STACK = `"Inter", ${SYSTEM_FONT_STACK}`;

/** Default typography scale (7 tiers per FRD Section 8.3) */
const DEFAULT_TYPOGRAPHY: TypographyConfig = {
  headingLarge: { size: "24px", weight: "700" },
  heading: { size: "16px", weight: "600" },
  body: { size: "14px", weight: "400" },
  bodyMedium: { size: "14px", weight: "500" },
  bodySmall: { size: "12px", weight: "400" },
  caption: { size: "12px", weight: "400" },
  labelSmall: { size: "11px", weight: "500" },
};

/** Opening copy required for beta UAT across all entity widgets. */
export function buildBetaOpeningMessage(entityName: string): string {
  return (
    `Hello! I am Hana, your ${entityName} care assistant. ` +
    "How may I assist you today?\n\n" +
    "As I am currently undergoing beta testing, I appreciate your patience " +
    "as we continue to improve your experience.\n\n" +
    'Please reply "OK" to proceed after reading and accepting our [Terms of Use](https://www.hmimedical.com/hana-terms-of-use).'
  );
}

/** Default branding applied when no entity-specific branding is provided */
const DEFAULT_BRANDING: Required<EntityBranding> = {
  primaryColor: "#1565C0",
  primaryHover: "#1565C0",
  secondaryColor: "#42A5F5",
  backgroundColor: "#FFFFFF",
  surfaceColor: "#F5F5F5",
  textColor: "#212121",
  textSecondary: "#757575",
  borderColor: "#E0E0E0",
  botBubbleColor: "#F5F5F5",
  suggestedPillBackground: "#F5F5F5",
  suggestedPillBorder: "#E0E0E0",
  ctaRadius: 8,
  logoUrl: "",
  launcherIconUrl: "",
  displayName: "HANA",
  welcomeMessage: buildBetaOpeningMessage("HMI healthcare navigation"),
  fontFamily: SYSTEM_FONT_STACK,
  buttonRadius: 12,
  typography: DEFAULT_TYPOGRAPHY,
  pendingUpstreamBranding: false,
  // REQ-D-046: default empty-state starter pills. Generic-intent,
  // entity-agnostic; entity presets below override with tailored
  // copy. An unknown entity falls back to these so the empty state
  // is never cold.
  suggestedPrompts: [
    "OK",
    "Where are you located?",
    "What are your operating hours?",
    "I'd like to book an appointment",
  ],
};

/**
 * Entity branding presets for Wave 1 entities.
 *
 * EEC (Eagle Eye Centre): Orange, Outfit font, pill buttons, sparkle effects
 * DCCS (Dermatology Collective Clinic & Surgery): Claywood, Outfit font, pill buttons, warm neutrals
 * EAS (Eagle Aesthetics): EEC variant (same Orange family), Outfit font
 * HMI Main: Corporate blue, system font, rounded buttons, clean aesthetic
 * TOPS (The Orthopaedic Practice & Surgery): Green, Outfit font, pill buttons
 *
 * The key set MUST equal `entity_seeds.WAVE1_ENTITY_CONFIGS`. A preset for an
 * id the backend does not register renders locally and is then rejected at the
 * gateway (`rest_endpoints.py` validates against `WAVE1_ENTITY_IDS`), so it can
 * only ever mislead. Pinned by tests/regression/test_entity_registry_parity.py.
 */
export const ENTITY_PRESETS: Record<string, Partial<EntityBranding>> = {
  eec: {
    primaryColor: "#CA4B0C",
    primaryHover: "#B34309",
    secondaryColor: "#FBF6F1",
    backgroundColor: "#FFFFFF",
    surfaceColor: "#F8F3EF",
    textColor: "#603913",
    textSecondary: "#999999",
    borderColor: "#E0D5CC",
    botBubbleColor: "#F8F3EF",
    suggestedPillBackground: "#FBF6F1",
    suggestedPillBorder: "#E8DFD8",
    ctaRadius: 8,
    fontFamily: OUTFIT_FONT_STACK,
    buttonRadius: 20,
    displayName: "Eagle Eye Centre",
    welcomeMessage: buildBetaOpeningMessage("Eagle Eye Centre"),
    typography: DEFAULT_TYPOGRAPHY,
    // REQ-D-046: EEC (ophthalmology) starters — S1 wayfinding +
    // S3 booking intent. #648 renamed "eye check" → "eye consultation";
    // #647 added "eye screening" pill → routes to live agent.
    suggestedPrompts: [
      "OK",
      "Where is the Eagle Eye Centre?",
      "What are your operating hours?",
      "I'd like to book an eye consultation",
      "I need an eye screening",
      "Do you treat cataracts?",
    ],
  },
  dccs: {
    primaryColor: "#776754",
    primaryHover: "#5B5144",
    secondaryColor: "#FDFAF8",
    backgroundColor: "#FFFFFF",
    surfaceColor: "#FAF9F7",
    textColor: "#1A1A1A",
    textSecondary: "#999999",
    borderColor: "#E0D5CC",
    botBubbleColor: "#FAF9F7",
    suggestedPillBackground: "#FDFAF8",
    suggestedPillBorder: "#E0D5CC",
    ctaRadius: 8,
    fontFamily: OUTFIT_FONT_STACK,
    buttonRadius: 20,
    // HMI-supplied mark on the floating launcher, for EVERY DCCS embed on
    // every page of their site — not a per-page opt-in. Served from our own
    // widget path so we control the asset; `{apiEndpoint}` resolves to the
    // embed's own backend origin, so a UAT embed loads the UAT copy and a
    // production embed the production one. A host site can still override
    // per-page with `data-launcher-icon`; other entities are unaffected.
    launcherIconUrl: "{apiEndpoint}/widget/dccs-launcher.png",
    displayName: "Dermatology Collective Clinic & Surgery",
    welcomeMessage: buildBetaOpeningMessage(
      "Dermatology Collective Clinic & Surgery",
    ),
    typography: DEFAULT_TYPOGRAPHY,
    // REQ-D-046: DCCS (dermatology / skin) starters.
    suggestedPrompts: [
      "OK",
      "Where is the clinic located?",
      "What are your operating hours?",
      "I'd like to book a skin consultation",
      "What skin conditions do you treat?",
    ],
  },
  eas: {
    primaryColor: "#CA4B0C",
    primaryHover: "#B34309",
    secondaryColor: "#FBF6F1",
    backgroundColor: "#FFFFFF",
    surfaceColor: "#F8F3EF",
    textColor: "#603913",
    textSecondary: "#999999",
    borderColor: "#E0D5CC",
    botBubbleColor: "#F8F3EF",
    suggestedPillBackground: "#FBF6F1",
    suggestedPillBorder: "#E8DFD8",
    ctaRadius: 8,
    fontFamily: OUTFIT_FONT_STACK,
    buttonRadius: 20,
    displayName: "Eagle Aesthetics",
    welcomeMessage: buildBetaOpeningMessage("Eagle Aesthetics"),
    typography: DEFAULT_TYPOGRAPHY,
    // REQ-D-046: Eagle Aesthetics starters — aesthetic-treatment intent.
    suggestedPrompts: [
      "OK",
      "Where is Eagle Aesthetics located?",
      "What are your operating hours?",
      "I'd like to book an aesthetics consultation",
      "What aesthetic treatments do you offer?",
    ],
  },
  hmi_main: {
    // Browser-verified against the HANA II Figma frame
    // `HMI Medical_HANA_Style Guide` on 2026-05-24.
    primaryColor: "#0957CB",
    primaryHover: "#0047A8",
    secondaryColor: "#E8F3FF",
    backgroundColor: "#FFFFFF",
    surfaceColor: "#F8FAFF",
    textColor: "#3C3C3C",
    textSecondary: "#999999",
    borderColor: "#D7E3F6",
    botBubbleColor: "#F8FAFF",
    suggestedPillBackground: "#E8F3FF",
    suggestedPillBorder: "#BBD8FF",
    ctaRadius: 8,
    fontFamily: OUTFIT_FONT_STACK,
    buttonRadius: 20,
    displayName: "HMI Medical Group",
    welcomeMessage: buildBetaOpeningMessage("HMI Medical Group"),
    typography: DEFAULT_TYPOGRAPHY,
    pendingUpstreamBranding: false,
    // REQ-D-046: HMI Medical Group (general practice) starters.
    suggestedPrompts: [
      "OK",
      "Where are your clinics located?",
      "What are your operating hours?",
      "I'd like to book a health screening",
      "What services does HMI offer?",
    ],
  },
  tops: {
    primaryColor: "#1BAE70",
    primaryHover: "#189D65",
    secondaryColor: "#F4FBF8",
    backgroundColor: "#FFFFFF",
    surfaceColor: "#F8FDFB",
    textColor: "#14261C",
    textSecondary: "#4E5652",
    borderColor: "#E1EFE9",
    botBubbleColor: "#F8FDFB",
    suggestedPillBackground: "#F4FBF8",
    suggestedPillBorder: "#E4F5EE",
    ctaRadius: 8,
    // Their site uses Montserrat, but styles/fonts.ts only injects Outfit.
    fontFamily: OUTFIT_FONT_STACK,
    buttonRadius: 20,
    displayName: "The Orthopaedic Practice & Surgery",
    // "TOPS", not the legal name: "your The Orthopaedic Practice & Surgery
    // care assistant" is ungrammatical.
    welcomeMessage: buildBetaOpeningMessage("TOPS"),
    typography: DEFAULT_TYPOGRAPHY,
    pendingUpstreamBranding: false,
    suggestedPrompts: [
      "OK",
      "Where is TOPS located?",
      "What are your operating hours?",
      "I'd like to book an orthopaedic consultation",
    ],
  },
};

/** Default responsive breakpoint in pixels */
const DEFAULT_MOBILE_BREAKPOINT = 768;

/** Default panel width in pixels for desktop layout */
const DEFAULT_PANEL_WIDTH = 400;

/** Default toggle button position */
const DEFAULT_POSITION = "bottom-right" as const;

/** Default z-index for the widget container */
const DEFAULT_Z_INDEX = 10000;

/**
 * Default booking-flow rendering mode (REQ-C-024). "auto" lets the
 * widget pick form vs conversational based on field count.
 */
const DEFAULT_FORM_MODE = "auto" as const;

/**
 * Default field-count threshold for switching from conversational to
 * structured form rendering when formMode is "auto" (REQ-C-024).
 *
 * Chosen as 3 so a single-field "what's your phone number?" turn stays
 * conversational while a 5-field appointment booking flips to the
 * structured form. Operators can tune per-entity via the
 * `widget.form_mode_threshold` ConfigurableParameter (which the entity
 * config endpoint surfaces alongside branding).
 */
const DEFAULT_FORM_MODE_THRESHOLD = 3;

/**
 * REQ-D-046 — default shell mode. "panel" preserves the legacy
 * Concierge Panel behaviour for every existing snippet that only
 * passes `data-entity` + `data-api`.
 */
const DEFAULT_UI_MODE = "panel" as const;

/**
 * REQ-D-046 — default initial size. "expanded" preserves the
 * Wave-1 footprint so legacy embeds render unchanged.
 */
const DEFAULT_SIZE = "expanded" as const;

/** REQ-D-046 — opt-in expand toggle; default off for backwards compat. */
const DEFAULT_ALLOW_EXPAND = false;

/**
 * REQ-D-046 — validate-or-fallback for the `uiMode` enum. Any value
 * outside the declared `WidgetUiMode` set falls back to the default
 * and emits a console warn so the embedder sees the typo.
 */
function resolveUiMode(value: unknown): "panel" | "modal" {
  if (value === "panel" || value === "modal") return value;
  if (value !== undefined && value !== null) {
    console.warn(
      `[HANA Widget] Invalid uiMode="${String(value)}". ` +
        `Expected "panel" or "modal". Falling back to "${DEFAULT_UI_MODE}".`,
    );
  }
  return DEFAULT_UI_MODE;
}

/** REQ-D-046 — same shape as resolveUiMode for the size enum. */
function resolveSize(value: unknown): "compact" | "expanded" | "fullscreen" {
  if (value === "compact" || value === "expanded" || value === "fullscreen") {
    return value;
  }
  if (value !== undefined && value !== null) {
    console.warn(
      `[HANA Widget] Invalid defaultSize="${String(value)}". ` +
        `Expected "compact" | "expanded" | "fullscreen". ` +
        `Falling back to "${DEFAULT_SIZE}".`,
    );
  }
  return DEFAULT_SIZE;
}

/**
 * Resolve a partial WidgetConfig into a fully resolved config
 * with all defaults applied. Entity presets are merged first,
 * then user-provided branding overrides on top.
 */
export function resolveConfig(config: WidgetConfig): ResolvedWidgetConfig {
  const entityPreset = ENTITY_PRESETS[config.entityId] ?? {};

  const branding: Required<EntityBranding> = {
    ...DEFAULT_BRANDING,
    ...entityPreset,
    ...config.branding,
  };

  const touUrl =
    config.touUrl ?? ENTITY_TOU_URLS[config.entityId] ?? DEFAULT_TOU_URL;

  // REQ-C-024 — clamp the threshold to a sane integer >= 1 so a stray
  // 0 / negative / NaN config never collapses every form into a
  // conversational deluge.
  const rawThreshold = config.formModeThreshold ?? DEFAULT_FORM_MODE_THRESHOLD;
  const formModeThreshold =
    Number.isFinite(rawThreshold) && rawThreshold >= 1
      ? Math.floor(rawThreshold)
      : DEFAULT_FORM_MODE_THRESHOLD;

  return {
    entityId: config.entityId,
    apiEndpoint: config.apiEndpoint,
    branding,
    mobileBreakpoint: config.mobileBreakpoint ?? DEFAULT_MOBILE_BREAKPOINT,
    panelWidth: config.panelWidth ?? DEFAULT_PANEL_WIDTH,
    position: config.position ?? DEFAULT_POSITION,
    zIndex: config.zIndex ?? DEFAULT_Z_INDEX,
    touUrl,
    consentMessage: config.consentMessage ?? DEFAULT_CONSENT_MESSAGE,
    formMode: config.formMode ?? DEFAULT_FORM_MODE,
    formModeThreshold,
    harnessSessionId: config.harnessSessionId,
    testHarness: config.testHarness,
    // REQ-D-046 shell-variant fields. resolveUiMode / resolveSize fall back
    // to the legacy defaults on unknown values so a typo at the script-tag
    // surface (data-ui-mode="paneL") never bricks the embed.
    uiMode: resolveUiMode(config.uiMode),
    defaultSize: resolveSize(config.defaultSize),
    allowExpand:
      typeof config.allowExpand === "boolean"
        ? config.allowExpand
        : DEFAULT_ALLOW_EXPAND,
    triggerCtaSelector:
      typeof config.triggerCtaSelector === "string" &&
      config.triggerCtaSelector.length > 0
        ? config.triggerCtaSelector
        : null,
  };
}
